import { useEffect, useState } from 'react';
import api from '../../api';
import { Restaurant } from '../../models/restaurant';
import {
  Box,
  Button,
  Icon,
  Typography,
  useTheme,
  TextField,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
} from '@mui/material';
import { Link } from 'react-router-dom';
import paths from '../../routing/paths';
import AppButton from '../../components/AppButton';
import useNotification from '../../state/useNotification';
import DownloadTemplateButton from '../../components/DownloadTemplateButton';
import BulkUploadModal from './BulkUploadModal';
import CertifiedBadge from '../../components/CertifiedBadge';

const descriptionCutoffCharacters = 80;

const sortItems = (items: Restaurant[]): Restaurant[] => {
  return [...(items || [])].sort((a,b) => (a.title || '').toLowerCase() > (b.title || '').toLowerCase() ? 1 : -1);
}

const Restaurants = () => {
  const [items, setItems] = useState<Restaurant[]>([]);
  const [filteredItems, setFilteredItems] = useState<Restaurant[]>([]);
  const [nameFilter, setNameFilter] = useState('');
  const [certificationFilter, setCertificationFilter] = useState<string>('all');
  const [searchableFilter, setSearchableFilter] = useState<string>('all');
  const theme = useTheme();
  const [, setNotification] = useNotification();

  useEffect(() => {
    api.restaurant.getAll().then((items) => {
      setItems(sortItems(items));
      setFilteredItems(sortItems(items));
    });
  }, []);

  useEffect(() => {
    let filtered = [...items];

    if (nameFilter) {
      filtered = filtered.filter((item) =>
        item.title.toLowerCase().includes(nameFilter.toLowerCase()),
      );
    }

    if (certificationFilter === 'certified') {
      filtered = filtered.filter((item) => item.isCertified);
    } else if (certificationFilter === 'not-certified') {
      filtered = filtered.filter((item) => !item.isCertified);
    }

    if (searchableFilter === 'searchable') {
      filtered = filtered.filter((item) => item.searchable);
    } else if (searchableFilter === 'not-searchable') {
      filtered = filtered.filter((item) => !item.searchable);
    }

    setFilteredItems(filtered);
  }, [items, nameFilter, certificationFilter, searchableFilter]);

  const handleDelete = async (id: string) => {
    await api.restaurant.delete(id);
    setItems((items) => items.filter((item) => item.id !== id));
    setNotification({ message: 'Restaurant deleted' });
  };

  const handleRefreshItems = async () => {
    const items = await api.restaurant.getAll();
    setItems(sortItems(items));
  };

  return (
    <Box sx={{ p: 2 }}>
      <Typography variant="h6">
        Restaurants {!!filteredItems.length && `(${filteredItems.length})`}
      </Typography>

      <Box sx={{ display: 'flex', gap: 2, mb: 2, mt: 2, flexWrap: 'wrap' }}>
        <TextField
          label="Search by name"
          variant="outlined"
          size="small"
          value={nameFilter}
          onChange={(e) => setNameFilter(e.target.value)}
          sx={{ minWidth: 200 }}
        />
        <FormControl size="small" sx={{ minWidth: 180 }}>
          <InputLabel>Certification</InputLabel>
          <Select
            value={certificationFilter}
            label="Certification"
            onChange={(e) => setCertificationFilter(e.target.value)}
          >
            <MenuItem value="all">All</MenuItem>
            <MenuItem value="certified">Certified only</MenuItem>
            <MenuItem value="not-certified">Not certified</MenuItem>
          </Select>
        </FormControl>
        <FormControl size="small" sx={{ minWidth: 180 }}>
          <InputLabel>Searchable</InputLabel>
          <Select
            value={searchableFilter}
            label="Searchable"
            onChange={(e) => setSearchableFilter(e.target.value)}
          >
            <MenuItem value="all">All</MenuItem>
            <MenuItem value="searchable">Searchable</MenuItem>
            <MenuItem value="not-searchable">Not searchable</MenuItem>
          </Select>
        </FormControl>
      </Box>

      <Box sx={{ display: 'flex', justifyContent: 'flex-end', width: '100%', mb: 2, gap: 2 }}>
        <BulkUploadModal afterBulkUploadSuccess={handleRefreshItems} />
        <DownloadTemplateButton />
        <Link to={paths.restaurantPathBuilder('')}>
          <AppButton>
            <Icon sx={{ ml: 0.5 }}>add</Icon> Add new
          </AppButton>
        </Link>
      </Box>
      <Box>
        {filteredItems.map((item) => (
          <Box
            key={item.id}
            sx={{
              p: 1,
              mb: 1,
              pb: 2,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              borderBottom: `1px solid ${theme.palette.grey[300]}`,
            }}
          >
            <Box display="flex" alignItems="center" justifyContent="flex-start" gap={1}>
              {item.imageUrl && (
                <Box
                  sx={{
                    objectFit: 'cover',
                    height: 60,
                    width: 60,
                  }}
                  component="img"
                  src={item.imageUrl}
                />
              )}
              <Box>
                <Box display="flex" alignItems="center" justifyContent="flex-start" gap={1}>
                  <Typography variant="body1" fontWeight={500}>
                    {item.title}
                  </Typography>
                  {item.isCertified && <CertifiedBadge size={18} />}
                </Box>
                <Typography variant="body2" color={theme.palette.grey[500]}>
                  {item.description?.slice(0, descriptionCutoffCharacters)}
                  {item.description?.length > descriptionCutoffCharacters && '...'}
                </Typography>
              </Box>
            </Box>
            <Box display="flex" alignItems="center" justifyContent="flex-start" gap={1}>
              <Link to={paths.restaurantPathBuilder(item.id || '')}>
                <Button>Edit</Button>
              </Link>
              <Typography
                sx={{ cursor: 'pointer' }}
                color="error"
                onClick={() => handleDelete(item.id as string)}
              >
                <Button color="error">Delete</Button>
              </Typography>
            </Box>
          </Box>
        ))}
      </Box>
    </Box>
  );
};

export default Restaurants;
